import { STAFF_SESSION_KEYS } from './staffSession';

/**
 * The ceiling on sending someone to Auth0 without them pressing anything: ONE trip per tab, until a new
 * click on an invite link asks again.
 *
 * Evan, 2026-09-16: the invite screen treated every "no session" as "needs a sign-in", Auth0 already had
 * a session and answered instantly, the staff check refused the person again, and the tab went
 * authorize → token → /v1/org/me → 403 → authorize until it had made 549 requests. Two bugs — the refusal
 * that should never have gated the screen, and a retry with no ceiling. This is the ceiling: whatever the
 * next bug is, it can cost one round trip and a card, never a loop.
 *
 * `sessionStorage`, because each loop iteration is a new page load and nothing in memory survives one.
 * When storage cannot be read or written the answer is NO — the safe direction — and the screen draws a
 * sign-in button instead, which is also all that would work, since the invite token cannot be parked
 * across the trip either.
 */
function store(): Storage | undefined {
  try {
    return globalThis.sessionStorage;
  } catch {
    return undefined;
  }
}

export const automaticSignIn = {
  /** Yes at most once per tab; saying yes spends it. */
  take(): boolean {
    try {
      const s = store();
      if (!s || s.getItem(STAFF_SESSION_KEYS.automaticSignIn)) return false;
      s.setItem(STAFF_SESSION_KEYS.automaticSignIn, '1');
      return s.getItem(STAFF_SESSION_KEYS.automaticSignIn) === '1';
    } catch {
      return false;
    }
  },
  /** A fresh click on an invite link is a person asking again, so it earns one more trip. */
  reset(): void {
    try {
      store()?.removeItem(STAFF_SESSION_KEYS.automaticSignIn);
    } catch {
      // ignore: take() then says no, which is safe
    }
  },
};
